import { useState, useEffect, useMemo } from 'react';
import './GitHubContributions.css';

const GitHubContributions = ({ username }) => {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalContributions, setTotalContributions] = useState(0);

  useEffect(() => {
    const fetchContributions = async () => {
      try {
        const response = await fetch(
          `https://github-contributions-api.jogruber.de/v4/${username}?y=last`
        );
        if (!response.ok) throw new Error('Failed to fetch contributions');
        const data = await response.json();

        setTotalContributions(data.total?.lastYear ?? 0);
        setContributions(data.contributions || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching GitHub contributions:', err);
        setError('Failed to load GitHub contributions.');
        setLoading(false);
      }
    };

    if (username) fetchContributions();
  }, [username]);

  const { grid, monthLabels } = useMemo(() => {
    if (contributions.length === 0) return { grid: [], monthLabels: [] };

    const sorted = [...contributions].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const firstDay = new Date(sorted[0].date).getDay();
    const padded = [
      ...Array(firstDay).fill({ date: null, count: 0, level: -1 }),
      ...sorted,
    ];

    const weeks = [];
    for (let i = 0; i < padded.length; i += 7) {
      weeks.push(padded.slice(i, i + 7));
    }

    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const labels = [];
    let lastMonth = null;
    weeks.forEach((week, colIdx) => {
      const realDay = week.find(d => d.date);
      if (realDay) {
        const m = new Date(realDay.date).getMonth();
        if (m !== lastMonth) {
          labels.push({ label: months[m], col: colIdx });
          lastMonth = m;
        }
      }
    });

    return { grid: weeks, monthLabels: labels };
  }, [contributions]);

  if (loading) return <div className="github-loading">Loading contribution data...</div>;
  if (error) return <div className="github-error">{error}</div>;
  if (contributions.length === 0) return null;

  return (
    <div className="github-contributions-container">
      <div className="contribution-header">
        <h3><span>GitHub Contributions</span></h3>
      </div>

      <div className="graph-wrapper">
        <div className="day-labels">
          <span></span>
          <span>Mon</span>
          <span></span>
          <span>Wed</span>
          <span></span>
          <span>Fri</span>
          <span></span>
        </div>

        <div className="graph-area">
          <div className="months-container" style={{ gridTemplateColumns: `repeat(${grid.length}, 1fr)` }}>
            {grid.map((_, colIdx) => {
              const label = monthLabels.find(l => l.col === colIdx);
              return (
                <div key={colIdx} className="month-cell">
                  {label ? label.label : ''}
                </div>
              );
            })}
          </div>

          <div
            className="contributions-grid"
            style={{ gridTemplateColumns: `repeat(${grid.length}, 1fr)` }}
          >
            {grid.map((week, colIdx) =>
              week.map((day, rowIdx) => (
                <div
                  key={`${colIdx}-${rowIdx}`}
                  className={`contribution-cell ${day.level >= 0 ? `contribution-level-${day.level}` : 'contribution-empty'}`}
                  style={{ gridColumn: colIdx + 1, gridRow: rowIdx + 1 }}
                  title={
                    day.date
                      ? `${day.count} contribution${day.count !== 1 ? 's' : ''} on ${day.date}`
                      : ''
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div className="contribution-summary">
        <div className="contribution-count">
          {totalContributions} contributions in the last year
        </div>
        <div className="contribution-legend">
          <span className="contribution-legend-text">Less</span>
          <div className="legend-item contribution-level-0"></div>
          <div className="legend-item contribution-level-1"></div>
          <div className="legend-item contribution-level-2"></div>
          <div className="legend-item contribution-level-3"></div>
          <div className="legend-item contribution-level-4"></div>
          <span className="contribution-legend-text">More</span>
        </div>
      </div>
    </div>
  );
};

export default GitHubContributions;
